import * as THREE from 'three';
import { BLACK, basic, cloth, emis, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * La Plañidera (docs/CRIATURAS.md §2): the pack's healer. A tall, hunched widow under a mantilla raised to a
 * point by a high comb, a cracked porcelain mask weeping ink, a censer of cold lilac fire held up on its chains.
 * Every 2 s the fire swells and threads of ink-light reach out to the nightmares around her (the `heal` clip
 * blooms the `flare` bone). Opaque shared cloth for the bulk (the baker folds it into few draws), one
 * translucent material (the ink smoke) and one self-lit one (the fire, its threads and her eyes), plus glows.
 */

/** The cold fire: censer core, its threads and her eyes share it (one self-lit material). */
const FIRE = '#c7b4ff';
/** The censer's centre at rest (model coordinates), hung from the raised left fist. */
export const MOURNER = { fist: V(-0.27, 1.86, 0.36), censer: V(-0.27, 1.55, 0.36) };

/**
 * An open, ragged bell of cloth: an open cylinder whose hem is torn into points and whose wall folds in and out.
 * `theta` limits it to an arc (a drape that covers only the back).
 */
const drape = (rTop: number, rBot: number, h: number, folds: number, tear: number, rng: () => number, theta?: [number, number]): THREE.BufferGeometry => {
  const geo = theta ? new THREE.CylinderGeometry(rTop, rBot, h, 10, 3, true, theta[0], theta[1]) : new THREE.CylinderGeometry(rTop, rBot, h, 13, 3, true);
  const p = geo.attributes.position as THREE.BufferAttribute;
  const cuts = new Map<number, number>();
  for (let i = 0; i < p.count; i++) {
    const x = p.getX(i), y = p.getY(i), z = p.getZ(i);
    const a = Math.atan2(x, z);
    const k = (h / 2 - y) / h; // 0 at the top, 1 at the hem
    // Folds deepen toward the hem.
    const f = 1 + 0.09 * k * Math.sin(a * folds + 0.7) + 0.04 * k * Math.sin(a * folds * 2.3);
    p.setX(i, x * f);
    p.setZ(i, z * f);
    if (k > 0.99) {
      // Torn hem: every other column is a long point, the rest ragged notches (shared seam keeps its cut).
      const key = Math.round(a * 1000);
      let cut = cuts.get(key);
      if (cut === undefined) {
        cut = cuts.size % 2 ? -tear * (0.6 + rng() * 0.6) : tear * rng() * 0.5;
        cuts.set(key, cut);
      }
      p.setY(i, y + cut);
    }
  }
  geo.computeVertexNormals();
  return geo;
};

/**
 * Bones: body at the hips carries the skirt (robe, with the train dragging behind it) and the hunched chest; the
 * chest carries the head (and on it the mantilla's long back drape, `veil`), the raised censer arm (armL, foreL,
 * the censer swinging from the fist on its chains, and the `flare` of threads around its fire) and the free claw
 * (armR, foreR). No legs: she glides inside her skirts.
 */
export const mourner = (o?: BaseOpts): Model => {
  const c = begin(o, 0.62, 41);
  const { g, R, rng } = c;
  const gown = cloth('#17131d', { side: THREE.DoubleSide });
  const veilM = cloth('#251f2e', { side: THREE.DoubleSide });
  const lace = cloth('#322a3d', { side: THREE.DoubleSide });
  const skin = std('#4f4a59', { roughness: 0.85 });
  const nail = std('#b9b6c4', { roughness: 0.5 });
  const porcelain = std('#cfd0d8', { roughness: 0.35, bumpScale: 0.004 });
  const ink = std('#0a0810', { roughness: 0.12, metalness: 0.3, bumpScale: 0 });
  const iron = metal('#34333c', { roughness: 0.45 });
  const fire = emis('#c4b0ff', '#8c6cff', 1.6);

  const bodyJ = joint(g, V(0, 0.78, 0), 'body');
  const robeJ = joint(bodyJ.abs, V(0, 0.82, 0), 'robe');
  const trainJ = joint(robeJ.abs, V(0, 0.2, -0.3), 'train');
  const chestJ = joint(bodyJ.abs, V(0, 0.95, 0.0), 'chest');
  const ch = chestJ.abs;
  const headJ = joint(ch, V(0, 1.4, 0.18), 'head');
  const veilJ = joint(headJ.abs, V(0, 1.72, 0.16), 'veil');
  const shL = V(-0.17, 1.37, 0.12), elL = V(-0.37, 1.5, 0.28), fist = MOURNER.fist;
  const shR = V(0.17, 1.35, 0.12), elR = V(0.31, 1.1, 0.22), handR = V(0.25, 0.92, 0.4);
  const armLJ = joint(ch, shL, 'armL');
  const foreLJ = joint(armLJ.abs, elL, 'foreL');
  const censerJ = joint(foreLJ.abs, fist, 'censer');
  const C = MOURNER.censer;
  const flareJ = joint(censerJ.abs, C, 'flare');
  const armRJ = joint(ch, shR, 'armR');
  const foreRJ = joint(armRJ.abs, elR, 'foreR');

  // A pool of ink spreading under her hem: she leaves it wherever she stands.
  const pg = new THREE.CircleGeometry(0.5, 20);
  const pp = pg.attributes.position as THREE.BufferAttribute;
  for (let i = 1; i < pp.count; i++) {
    const f = 0.8 + 0.25 * Math.sin(i * 2.7) + rng() * 0.2;
    pp.setXY(i, pp.getX(i) * f, pp.getY(i) * f);
  }
  const pool = M(pg, ink, g);
  pool.rotation.x = -Math.PI / 2;
  pool.position.set(0, 0.006, -0.06);
  for (let i = 0; i < 4; i++) {
    const a = rng() * 6.3;
    const d = M(new THREE.CircleGeometry(0.035 + rng() * 0.03, 7), ink, g);
    d.rotation.x = -Math.PI / 2;
    d.position.set(Math.cos(a) * 0.58, 0.005, Math.sin(a) * 0.58 - 0.06);
  }

  // The skirt: a long bell of black-violet from the waist to the ground, torn at the hem, folded deep.
  const skirt = M(drape(0.16, 0.44, 0.92, 7, 0.14, rng), gown, robeJ.abs);
  skirt.position.set(0, 0.46, -0.02);
  // A second, lighter layer of mourning lace over it, shorter and more ragged.
  const over = M(drape(0.2, 0.4, 0.55, 5, 0.18, rng), lace, robeJ.abs);
  over.position.set(0, 0.62, 0.0);
  over.rotation.y = 0.4;
  // The train dragging behind her on the ground.
  const tg = new THREE.PlaneGeometry(0.5, 0.62, 4, 4);
  const tp = tg.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < tp.count; i++) {
    const y = tp.getY(i), x = tp.getX(i);
    // Bent: hangs from the skirt's back, then lies along the ground; the far end torn.
    const k = (0.31 - y) / 0.62;
    tp.setXYZ(i, x * (1 + 0.5 * k), 0.36 - Math.min(k, 0.45) * 0.75, -0.22 - Math.max(0, k - 0.45) * 0.62 - (k > 0.99 ? rng() * 0.12 : 0) + 0.03 * Math.sin(x * 20));
  }
  tg.computeVertexNormals();
  M(tg, gown, trainJ.abs);
  // A cord at the waist with a string of black beads hanging from it.
  const cord = M(new THREE.TorusGeometry(0.17, 0.014, 4, 10), lace, bodyJ.abs);
  cord.rotation.x = Math.PI / 2 - 0.15;
  cord.position.set(0, 0.88, 0.03);
  for (let i = 0; i < 6; i++) M(new THREE.OctahedronGeometry(0.017, 0), ink, robeJ.abs).position.set(0.1 + i * 0.01, 0.84 - i * 0.052, 0.15 + i * 0.012);
  M(new THREE.OctahedronGeometry(0.03, 0), iron, robeJ.abs).position.set(0.16, 0.52, 0.22);

  // The hunched trunk: a bent back with a hump, the shoulders thrust forward.
  const torso = M(new THREE.SphereGeometry(0.19, 10, 7), gown, ch);
  torso.scale.set(1.05, 1.35, 0.85);
  torso.position.set(0, 1.16, 0.05);
  torso.rotation.x = 0.4;
  const hump = M(new THREE.SphereGeometry(0.17, 8, 6), gown, ch);
  hump.position.set(0, 1.3, -0.03);
  hump.scale.set(1.1, 0.9, 1);
  // A shawl of veil over the shoulders, falling past the hips in torn points.
  const shawl = M(drape(0.12, 0.36, 0.72, 6, 0.16, rng), veilM, ch);
  shawl.position.set(0, 1.12, 0.03);
  shawl.rotation.x = 0.25;

  // The head, a size up (it has to read on a phone): a deep hood, a weeping porcelain mask inside it.
  const hd = G(headJ.abs);
  const HC = V(0, 1.5, 0.26), HS = 1.22;
  hd.scale.setScalar(HS);
  hd.position.copy(HC).multiplyScalar(1 - HS).add(V(0, -0.03, 0.03));
  const hoodM = M(new THREE.SphereGeometry(0.15, 12, 9, Math.PI / 2 + 0.95, Math.PI * 2 - 1.9, 0, Math.PI / 1.3), veilM, hd);
  hoodM.position.set(0, 1.52, 0.22);
  hoodM.scale.set(1, 1.12, 1);
  hoodM.rotation.x = -0.15;
  M(new THREE.SphereGeometry(0.11, 7, 5), BLACK, hd).position.set(0, 1.5, 0.22);
  const mask = M(new THREE.SphereGeometry(0.1, 10, 8, Math.PI / 2 - 1.05, 2.1, 0.35, Math.PI - 0.6), porcelain, hd);
  mask.scale.set(0.82, 1.2, 0.7);
  mask.position.set(0, 1.49, 0.26);
  mask.rotation.x = 0.18;
  // Hollow eyes with a cold light deep in them, brows sloped in grief, a mouth turned down.
  for (const k of [-1, 1]) {
    const so = M(new THREE.SphereGeometry(0.021, 6, 4), BLACK, hd);
    so.scale.set(1.3, 0.75, 0.6);
    so.position.set(k * 0.033, 1.515, 0.327);
    so.rotation.z = k * 0.35;
    const br = M(new THREE.BoxGeometry(0.045, 0.007, 0.01), BLACK, hd);
    br.position.set(k * 0.035, 1.543, 0.322);
    br.rotation.z = -k * 0.4;
    // Tears of ink running from the eyes down the mask and dripping off the chin.
    limb(hd, V(k * 0.036, 1.5, 0.33), V(k * 0.042, 1.45, 0.322), 0.0055, 0.0045, ink, 4);
    limb(hd, V(k * 0.042, 1.45, 0.322), V(k * 0.036, 1.4, 0.3), 0.0045, 0.004, ink, 4);
    const drop = M(new THREE.SphereGeometry(0.009, 4, 3), ink, hd);
    drop.position.set(k * 0.036, 1.392, 0.298);
    drop.scale.y = 1.5;
  }
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.009, 5, 4), fire, hd).position.set(k * 0.033, 1.513, 0.334);
    glowSprite(hd, FIRE, 0.07, V(k * 0.033, 1.513, 0.344), 0.9);
  }
  const mouth = M(new THREE.TorusGeometry(0.024, 0.0045, 3, 6, Math.PI), BLACK, hd);
  mouth.position.set(0, 1.435, 0.322);
  mouth.rotation.x = 0.2;
  // A crack across the porcelain.
  limb(hd, V(0.01, 1.6, 0.31), V(0.026, 1.565, 0.326), 0.003, 0.003, BLACK, 3);
  limb(hd, V(0.026, 1.565, 0.326), V(0.012, 1.53, 0.335), 0.003, 0.003, BLACK, 3);
  // The high mourning comb behind the head, black iron edged with thorns: the veil hangs from it.
  const comb = G(headJ.abs, 0, 1.68, 0.1);
  comb.rotation.x = -0.25;
  // Open-work, an arched rim on five spokes, so the hood shows through it.
  const rimC = M(new THREE.TorusGeometry(0.22, 0.016, 4, 12, Math.PI), iron, comb);
  rimC.position.y = -0.02;
  for (let i = 0; i < 5; i++) {
    const a = (i / 4 - 0.5) * 2.4;
    limb(comb, V(Math.sin(a) * 0.05, -0.02 + Math.cos(a) * 0.05, 0), V(Math.sin(a) * 0.22, -0.02 + Math.cos(a) * 0.22, 0), 0.009, 0.007, iron, 3);
  }
  for (let i = 0; i < 9; i++) {
    const a = (i / 8 - 0.5) * 2.9;
    const len = 0.11 + (i % 2 ? 0 : 0.07) + (i === 4 ? 0.06 : 0);
    const th = M(new THREE.ConeGeometry(0.013, len, 4), iron, comb);
    th.position.set(Math.sin(a) * (0.22 + len / 2), -0.02 + Math.cos(a) * (0.22 + len / 2), 0);
    th.rotation.z = -a;
  }
  // The mantilla: from under the comb a long veil falls down her back to the hips.
  const back = M(drape(0.2, 0.36, 0.9, 5, 0.16, rng, [Math.PI / 2 + 0.1, Math.PI - 0.2]), veilM, veilJ.abs);
  back.position.set(0, 1.2, 0.03);
  back.rotation.x = 0.32;
  // Ragged strips of lace hanging from the veil's edge.
  for (let i = 0; i < 5; i++) {
    const a = Math.PI / 2 + 0.4 + i * 0.55;
    const s = M(new THREE.PlaneGeometry(0.05, 0.28 + rng() * 0.15), lace, veilJ.abs);
    s.position.set(Math.sin(a) * 0.36, 0.72 + R() * 0.06, Math.cos(a) * 0.36 - 0.12);
    s.rotation.set(0.15, a, R() * 0.2);
  }

  // The free arm: thin, hanging forward, a mourning sleeve falling from the elbow, long clawed fingers.
  limb(armRJ.abs, shR, elR, 0.05, 0.04, gown, 6);
  limb(foreRJ.abs, elR, handR, 0.032, 0.026, skin, 6);
  const slR = M(drape(0.05, 0.13, 0.42, 4, 0.08, rng), veilM, foreRJ.abs);
  slR.position.set(elR.x + 0.01, elR.y - 0.17, elR.z + 0.02);
  slR.rotation.x = -0.25;
  M(new THREE.SphereGeometry(0.032, 6, 4), skin, foreRJ.abs).position.copy(handR);
  for (let f = 0; f < 4; f++) {
    const sp = (f - 1.5) * 0.3;
    const k1 = handR.clone().add(V(Math.sin(sp) * 0.04, -0.06, 0.05 + Math.cos(sp) * 0.02));
    const k2 = k1.clone().add(V(Math.sin(sp) * 0.03, -0.07, 0.04));
    limb(foreRJ.abs, handR, k1, 0.011, 0.009, skin, 3);
    limb(foreRJ.abs, k1, k2, 0.009, 0.006, skin, 3);
    const cl = M(new THREE.ConeGeometry(0.007, 0.05, 4), nail, foreRJ.abs);
    cl.position.copy(k2).add(V(0, -0.015, 0.02));
    cl.rotation.x = 2.4;
  }

  // The censer arm: raised, the sleeve fallen back down to the elbow, the fist clenched on the chains.
  limb(armLJ.abs, shL, elL, 0.05, 0.04, gown, 6);
  limb(foreLJ.abs, elL, fist, 0.032, 0.027, skin, 6);
  const slL = M(drape(0.06, 0.14, 0.38, 4, 0.08, rng), veilM, foreLJ.abs);
  slL.position.set(elL.x - 0.01, elL.y - 0.08, elL.z - 0.02);
  slL.rotation.set(0.2, 0, -0.25);
  M(new THREE.SphereGeometry(0.036, 6, 4), skin, foreLJ.abs).position.copy(fist);
  for (let f = 0; f < 3; f++) {
    const cl = M(new THREE.ConeGeometry(0.008, 0.045, 4), nail, foreLJ.abs);
    cl.position.set(fist.x + 0.03, fist.y - 0.01 - f * 0.018, fist.z + 0.02);
    cl.rotation.z = 1.9;
  }

  // The censer: an iron bowl and a cage of bars around a cold lilac fire, on three chains from the fist.
  const cs = censerJ.abs;
  const bowl = M(new THREE.SphereGeometry(0.078, 10, 3, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2), iron, cs);
  bowl.position.copy(C);
  M(new THREE.CylinderGeometry(0.025, 0.04, 0.035, 8), iron, cs).position.set(C.x, C.y - 0.09, C.z);
  const rim = M(new THREE.TorusGeometry(0.078, 0.008, 3, 12), iron, cs);
  rim.position.copy(C).setY(C.y + 0.005);
  rim.rotation.x = Math.PI / 2;
  M(new THREE.SphereGeometry(0.06, 8, 6), fire, cs).position.copy(C).setY(C.y + 0.02);
  const top = V(C.x, C.y + 0.13, C.z);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const r0 = V(C.x + Math.cos(a) * 0.075, C.y + 0.005, C.z + Math.sin(a) * 0.075);
    const mid = V(C.x + Math.cos(a) * 0.06, C.y + 0.08, C.z + Math.sin(a) * 0.06);
    limb(cs, r0, mid, 0.006, 0.006, iron, 3);
    limb(cs, mid, top, 0.006, 0.005, iron, 3);
  }
  const knob = M(new THREE.ConeGeometry(0.02, 0.05, 6), iron, cs);
  knob.position.copy(top).setY(top.y + 0.02);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + 0.5;
    limb(cs, V(C.x + Math.cos(a) * 0.075, C.y + 0.005, C.z + Math.sin(a) * 0.075), V(fist.x, fist.y - 0.03, fist.z), 0.004, 0.004, iron, 3);
  }
  const glow = glowSprite(cs, '#a88cff', 0.85, V(C.x, C.y + 0.02, C.z), 0.75, true);
  const hot = glowSprite(cs, '#e6dcff', 0.28, V(C.x, C.y + 0.02, C.z), 0.95, true);
  // The flare: threads of the fire spilling over the censer's rim, and a halo; the heal clip blooms them out.
  const fl = flareJ.abs;
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 + 0.3;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 7; j++) {
      const s = j / 6;
      // Up out of the fire, then arching out and down like a willow (bloomed, they reach her kin).
      const r = 0.04 + s * 0.12;
      const b = a + s * 0.7;
      pts.push(V(C.x + Math.cos(b) * r, C.y + 0.04 + 0.06 * s - 0.17 * s * s + 0.012 * Math.sin(s * 9 + i), C.z + Math.sin(b) * r));
    }
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), 7, 0.0035, 3), fire, fl);
  }
  const halo = M(new THREE.TorusGeometry(0.12, 0.004, 3, 12), fire, fl);
  halo.rotation.x = Math.PI / 2;
  halo.position.copy(C).setY(C.y - 0.03);

  // Ink smoke: pouring heavy out of the censer's bowl, and boiling up off the hem.
  const ribs: Ribbon[] = [];
  const wm = basic('#2a2140', 0.5);
  for (let i = 0; i < 2; i++) {
    const a = i * 3.1;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 6; j++) pts.push(V(C.x + Math.cos(a) * 0.03 * (1 + j * 0.6) + Math.sin(j * 1.3 + i) * 0.03, C.y - 0.08 - j * 0.1, C.z + Math.sin(a) * 0.03 * (1 + j * 0.6)));
    ribs.push(new Ribbon(cs, pts, 0.016, wm, rng, 0.025));
  }
  for (let i = 0; i < 3; i++) {
    const a = i * 2.1 + 0.3;
    const pts: THREE.Vector3[] = [];
    for (let j = 0; j < 6; j++) pts.push(V(Math.sin(a) * (0.42 - j * 0.03) + Math.sin(j * 0.9 + i) * 0.05, 0.02 + j * 0.11, Math.cos(a) * (0.42 - j * 0.03) - 0.04));
    ribs.push(new Ribbon(robeJ.abs, pts, 0.024, wm, rng, 0.045));
  }
  glowSprite(ch, '#7a5cff', 0.9, V(0, 1.15, 0.1), 0.16);

  // Only the boil here: the swing of the censer, the bloom of the threads and the glide are the clips'.
  const t = (): void => {
    for (const r of ribs) r.step();
    glow.material.opacity = 0.6 + rng() * 0.3;
    hot.material.opacity = 0.8 + rng() * 0.2;
  };
  t();
  return done(c, 1.9, t, undefined, bonesOf(c, {
    body: bodyJ.p, robe: robeJ.p, train: trainJ.p, chest: chestJ.p, head: headJ.p, veil: veilJ.p,
    armL: armLJ.p, foreL: foreLJ.p, censer: censerJ.p, flare: flareJ.p, armR: armRJ.p, foreR: foreRJ.p,
  }));
};
